import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Save, 
  Loader2, 
  Mail, 
  MessageSquare, 
  Smartphone, 
  Eye,
  X
} from 'lucide-react';
import { SlidePanel } from '@/components/ui/slide-panel';
import { TemplateSetService, TemplateSetItem } from '@/services/templateSetService';

interface TemplateSetItemEditorProps {
  templateSetId: string;
  templateSetName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function TemplateSetItemEditor({ 
  templateSetId, 
  templateSetName, 
  isOpen, 
  onClose 
}: TemplateSetItemEditorProps) {
  const [templates, setTemplates] = useState<TemplateSetItem[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateSetItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateSetItem | null>(null);

  useEffect(() => {
    if (isOpen && templateSetId) {
      fetchTemplates();
    }
  }, [isOpen, templateSetId]);

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      const items = await TemplateSetService.getTemplateSetItems(templateSetId);
      setTemplates(items);
      if (items.length > 0 && !selectedTemplate) {
        setSelectedTemplate(items[0]);
        setEditingTemplate({ ...items[0] });
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSelect = (template: TemplateSetItem) => {
    setSelectedTemplate(template);
    setEditingTemplate({ ...template });
  };

  const handleSave = async () => {
    if (!editingTemplate) return;

    try {
      setIsSaving(true);
      await TemplateSetService.updateTemplateSetItem(editingTemplate.id, {
        title_template: editingTemplate.title_template,
        message_template: editingTemplate.message_template,
        email_subject_template: editingTemplate.email_subject_template,
        email_body_template: editingTemplate.email_body_template,
        sms_template: editingTemplate.sms_template,
        slack_template: editingTemplate.slack_template,
        description: editingTemplate.description,
        variables: editingTemplate.variables
      });

      // Update the local state
      setTemplates(prev => prev.map(t => 
        t.id === editingTemplate.id ? editingTemplate : t
      ));
      setSelectedTemplate(editingTemplate);

      toast.success('Template updated successfully!');
    } catch (error: any) {
      console.error('Error saving template:', error);
      toast.error(error.message || 'Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: keyof TemplateSetItem, value: string | string[]) => {
    if (!editingTemplate) return;
    setEditingTemplate(prev => prev ? { ...prev, [field]: value } : null);
  };

  const hasChanges = editingTemplate && selectedTemplate && (
    editingTemplate.title_template !== selectedTemplate.title_template ||
    editingTemplate.message_template !== selectedTemplate.message_template ||
    editingTemplate.email_subject_template !== selectedTemplate.email_subject_template ||
    editingTemplate.email_body_template !== selectedTemplate.email_body_template ||
    editingTemplate.sms_template !== selectedTemplate.sms_template ||
    editingTemplate.slack_template !== selectedTemplate.slack_template ||
    editingTemplate.description !== selectedTemplate.description
  );

  return (
    <SlidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Edit Templates: {templateSetName}
        </div>
      }
      width="2xl"
      topOffset="4.5rem"
      showEdgeCloseButton={true}
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Edit individual notification templates within this template set
        </p>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading templates...</span>
          </div>
        ) : (
          <div className="flex h-[70vh]">
            {/* Template List */}
            <div className="w-1/3 border-r">
              <div className="p-4 border-b">
                <h3 className="font-medium text-foreground">Templates ({templates.length})</h3>
              </div>
              <ScrollArea className="h-full">
                <div className="p-2 space-y-1">
                  {templates.map((template) => (
                    <Button
                      key={template.id}
                      variant={selectedTemplate?.id === template.id ? "secondary" : "ghost"}
                      className={`w-full justify-start text-left h-auto p-3 ${
                        selectedTemplate?.id === template.id 
                          ? 'bg-primary/15 border border-primary/30 text-primary-foreground hover:bg-primary/20' 
                          : 'hover:bg-muted border-transparent'
                      }`}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <div className="space-y-1">
                        <div className="font-medium text-sm text-foreground">
                          {template.notification_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </div>
                        {template.description && (
                          <div className="text-xs text-muted-foreground line-clamp-2">
                            {template.description}
                          </div>
                        )}
                        <div className="flex gap-1">
                          <Badge variant="outline" className="text-xs">
                            {template.variables.length} vars
                          </Badge>
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Template Editor */}
            <div className="flex-1 flex flex-col">
              {editingTemplate ? (
                <>
                  <div className="p-4 border-b">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-foreground">
                          {editingTemplate.notification_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Variables: {editingTemplate.variables.join(', ')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {hasChanges && (
                          <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">Unsaved changes</Badge>
                        )}
                        <Button 
                          onClick={handleSave} 
                          disabled={isSaving || !hasChanges}
                          size="sm"
                        >
                          {isSaving ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Save Changes
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <ScrollArea className="flex-1 p-4">
                    <Tabs defaultValue="basic" className="space-y-4">
                      <TabsList>
                        <TabsTrigger value="basic">Basic</TabsTrigger>
                        <TabsTrigger value="email">Email</TabsTrigger>
                        <TabsTrigger value="sms">SMS</TabsTrigger>
                        <TabsTrigger value="slack">Slack</TabsTrigger>
                      </TabsList>

                      <TabsContent value="basic" className="space-y-4">
                        <div>
                          <Label htmlFor="title">Title Template</Label>
                          <Input
                            id="title"
                            value={editingTemplate.title_template}
                            onChange={(e) => handleInputChange('title_template', e.target.value)}
                            placeholder="Template title..."
                          />
                        </div>
                        <div>
                          <Label htmlFor="message">Message Template</Label>
                          <Textarea
                            id="message"
                            value={editingTemplate.message_template}
                            onChange={(e) => handleInputChange('message_template', e.target.value)}
                            placeholder="Template message..."
                            rows={4}
                          />
                        </div>
                        <div>
                          <Label htmlFor="description">Description</Label>
                          <Input
                            id="description"
                            value={editingTemplate.description || ''}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Template description..."
                          />
                        </div>
                      </TabsContent>

                      <TabsContent value="email" className="space-y-4">
                        <div>
                          <Label htmlFor="email-subject">Email Subject</Label>
                          <Input
                            id="email-subject"
                            value={editingTemplate.email_subject_template || ''}
                            onChange={(e) => handleInputChange('email_subject_template', e.target.value)}
                            placeholder="Email subject template..."
                          />
                        </div>
                        <div>
                          <Label htmlFor="email-body">Email Body (HTML)</Label>
                          <Textarea
                            id="email-body"
                            value={editingTemplate.email_body_template || ''}
                            onChange={(e) => handleInputChange('email_body_template', e.target.value)}
                            placeholder="HTML email body template..."
                            rows={12}
                            className="font-mono text-sm"
                          />
                        </div>
                      </TabsContent>

                      <TabsContent value="sms" className="space-y-4">
                        <div>
                          <Label htmlFor="sms">SMS Template</Label>
                          <Textarea
                            id="sms"
                            value={editingTemplate.sms_template || ''}
                            onChange={(e) => handleInputChange('sms_template', e.target.value)}
                            placeholder="SMS template..."
                            rows={6}
                          />
                          <p className="text-sm text-muted-foreground mt-2">
                            Keep SMS messages concise. Character limits apply for SMS delivery.
                          </p>
                        </div>
                      </TabsContent>

                      <TabsContent value="slack" className="space-y-4">
                        <div>
                          <Label htmlFor="slack">Slack Template</Label>
                          <Textarea
                            id="slack"
                            value={editingTemplate.slack_template || ''}
                            onChange={(e) => handleInputChange('slack_template', e.target.value)}
                            placeholder="Slack template..."
                            rows={8}
                          />
                          <p className="text-sm text-muted-foreground mt-2">
                            Use Slack markdown formatting. Supports *bold*, _italic_, and emoji.
                          </p>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </ScrollArea>
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium">Select a Template</h3>
                    <p className="text-muted-foreground">Choose a template from the list to start editing</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-4 mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </SlidePanel>
  );
}